import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api';
import { FormRow, PrimaryButton, SettingsCard, SuccessHint, inputStyle } from './_shared';

export function IdentityPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [form, setForm] = useState({
    display_name: '',
    organization_name: '',
    organization_legal_name: '',
    organization_vat_or_id: '',
    organization_contact_email: '',
    organization_contact_phone: '',
    organization_address: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const s = settingsQuery.data.settings;
    setForm({
      display_name: s.display_name ?? '',
      organization_name: s.organization.name ?? '',
      organization_legal_name: s.organization.legal_name ?? '',
      organization_vat_or_id: s.organization.vat_or_id ?? '',
      organization_contact_email: s.organization.contact_email ?? '',
      organization_contact_phone: s.organization.contact_phone ?? '',
      organization_address: s.organization.address ?? '',
    });
  }, [settingsQuery.data]);

  const mutation = useMutation({
    mutationFn: () => api.updateIdentity(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  return (
    <div className="space-y-6">
      <SettingsCard
        kicker="Identità"
        title="Identità installazione"
        description="Dati che appaiono nei PDF report, nelle email transazionali, e nel footer brand."
        rightSlot={<SuccessHint visible={showSuccess} />}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <FormRow
            label="Nome organizzazione"
            hint="Visualizzato in chat e PDF, es. Studio Bianchi"
          >
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.organization_name}
              onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Display name (interno)" hint="Etichetta nell'admin">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Ragione sociale legale">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.organization_legal_name}
              onChange={(e) => setForm((f) => ({ ...f, organization_legal_name: e.target.value }))}
            />
          </FormRow>
          <FormRow label="P.IVA / Codice fiscale">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.organization_vat_or_id}
              onChange={(e) => setForm((f) => ({ ...f, organization_vat_or_id: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Email contatto">
            <input
              type="email"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.organization_contact_email}
              onChange={(e) =>
                setForm((f) => ({ ...f, organization_contact_email: e.target.value }))
              }
            />
          </FormRow>
          <FormRow label="Telefono contatto">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={form.organization_contact_phone}
              onChange={(e) =>
                setForm((f) => ({ ...f, organization_contact_phone: e.target.value }))
              }
            />
          </FormRow>
          <div className="md:col-span-2">
            <FormRow label="Indirizzo">
              <textarea
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
                rows={2}
                value={form.organization_address}
                onChange={(e) => setForm((f) => ({ ...f, organization_address: e.target.value }))}
              />
            </FormRow>
          </div>
          {error && (
            <div className="md:col-span-2 text-sm" style={{ color: 'var(--danger)' }}>
              {error}
            </div>
          )}
          <div className="md:col-span-2">
            <PrimaryButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Salvo…' : 'Salva'}
            </PrimaryButton>
          </div>
        </form>
      </SettingsCard>
    </div>
  );
}
